/** @jsx jsx */
import React, {
	memo,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useImperativeHandle,
	type PropsWithChildren,
} from 'react';
import { cssMap, jsx, css, cx } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { Label } from '@atlaskit/form';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Flex, xcss, Box } from '@atlaskit/primitives';
import { type StylesConfig, components } from '@atlaskit/select';
import SimpleTag from '@atlaskit/tag/simple-tag';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { useMultiSelectKeyDown } from '@atlassian/jira-business-fields/src/common/cell-wrapper/utils.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { getPopupComponentWithHeight } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { TagListWrapper } from '@atlassian/jira-business-fields/src/common/ui/tag-list-wrapper/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useLabelsField } from '@atlassian/jira-issue-field-labels/src/services/labels-field-service/index.tsx';
import type { SelectOption } from '@atlassian/jira-issue-field-select/src/ui/edit/types.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import {
	memoizeGetAutoGeneratedColorForLabels,
	memoizeGetAutoGeneratedColorForLabelsTag,
} from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import LazyCreatableSelect from '../../../common/ui/lazy-creatable-select/index.tsx';
import { CellContentWrapper } from '../../../common/ui/list-cell/styled.tsx';
import { useLabels } from '../../../controllers/labels/index.tsx';
import messages from './messages.tsx';
import type { LabelsCellProps } from './types.tsx';

const defaultIssueLabels: Array<string> = [];

const POPUP_OFFSET_NEW: [number, number] = [0, -DEFAULT_ROW_HEIGHT];

const multiValueRemoveStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg': {
			transform: 'scale(1.5)',
			fill: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg > g > path': {
			d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
			transform: 'translate(5px, 6px)',
			stroke: token('color.text'),
			strokeWidth: '1.5',
		},
	},
});

const multiValueStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div > div': {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
	isVisualRefreshDisabled: {
		borderRadius: token('border.radius.100'),
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			paddingLeft: token('space.050'),
			paddingRight: token('space.0'),
			paddingTop: token('space.0'),
			paddingBottom: token('space.0'),
		},
	},
});

const multiValueLabelStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			borderColor: 'transparent',
		},
	},
});

const LabelsCell = memo<LabelsCellProps>(
	({
		issueKey,
		issueId,
		issueLabels = defaultIssueLabels,
		width,
		fieldType,
		fieldKey,
		isSelected = false,
		isEditable,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode = false,
		setEditMode = noop,
		isHovering = false,
		cellRef,
		inputValue,
		onInputChange,
	}: LabelsCellProps) => {
		const inputRef = useRef<HTMLInputElement>(null);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();
		const [{ error, value }, { saveValue, resetError }] = useLabelsField({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: issueLabels,
			issueId,
			onFailure: () => setLabels(issueLabels),
		});

		const { defaultSuggestions, addDefaultSuggestion, getSuggestions } = useLabels(
			issueId,
			fieldKey,
		);

		// Note the type here is not LabelsValue as it's impossible for updatedLabels to be null
		const updatedLabels: string[] = useMemo<string[]>(
			(): string[] => value || defaultIssueLabels,
			[value],
		);

		const [menuIsOpen, setMenuIsOpen] = useState(editMode);
		const wasEditMode = usePreviousWithInitial(editMode);

		useEffect(() => {
			setMenuIsOpen(editMode);
		}, [editMode]);

		// Note the type here is not LabelsValue as it's impossible for labels to be null
		const [labels, setLabels] = useState<string[]>((): string[] => updatedLabels);

		// Update the internal state in response to external data changes. Note
		// that setting state here is more efficient than with a `useEffect`
		// hook - see
		// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
		if (!editMode && !isEqual(labels, updatedLabels) && !wasEditMode) {
			setLabels(updatedLabels);
		}

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (!isEqual(labels.slice().sort(), updatedLabels.slice().sort())) {
				saveValue(labels, null, createAnalyticsEvent({}))
					.then(() => {
						labels
							.filter((label: string) => issueLabels.indexOf(label) === -1)
							.forEach((suggestion) => {
								addDefaultSuggestion(suggestion);
							});
					}) // Replace with lodash/noop
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					.catch(() => {});
			}
		}, [
			setEditMode,
			labels,
			updatedLabels,
			saveValue,
			createAnalyticsEvent,
			issueLabels,
			addDefaultSuggestion,
		]);

		const onEscapeKeyPress = useCallback(() => {
			setEditMode(false);
			setLabels(issueLabels);
		}, [issueLabels, setEditMode]);

		const { onKeyDown: onMultiSelectKeyDown, saveOnBlur } = useMultiSelectKeyDown({
			editMode,
			wasEditMode,
			menuIsOpen,
			setMenuIsOpen,
			saveAndClose,
			onEscapeKeyPress,
		});
		useEffect(saveOnBlur, [saveOnBlur]);

		const cellOverrides = useCallback(
			() => ({ onKeyDown: onMultiSelectKeyDown }),
			[onMultiSelectKeyDown],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const handleLabelsChange = useCallback(
			(newValue: SelectOption | readonly SelectOption[] | null) => {
				resetError();
				if (newValue) {
					setLabels(
						Array.isArray(newValue)
							? newValue.map((option) => String(option.label)).sort()
							: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								[(newValue as SelectOption).label],
					);
				} else {
					setLabels([]);
				}
			},
			[resetError],
		);

		const handleCreateOption = useCallback(
			(label: string) => {
				setLabels([...labels, label].sort());
			},
			[labels],
		);

		const handleInputRef = useCallback(
			(ref: Ref<'input'>) => {
				if (inputRef) {
					// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
					inputRef.current = ref;
				}
			},
			[inputRef],
		);

		useEffect(() => {
			if (editMode) {
				inputRef?.current?.focus();
			}
		}, [editMode, issueLabels]);

		const staticContent = useMemo(
			() =>
				labels.map((label) => {
					if (
						expVal(
							'adding-color-to-labels-field-in-jira-experiment',
							'isColoredLabelsEnabled',
							false,
						)
					) {
						if (isVisualRefreshEnabled()) {
							return (
								<Box as="span" xcss={tagContainerStyles} key={label}>
									{/* @ts-expect-error - ts2322 - Type 'string | undefined' is not assignable to type 'TagColor'.' */}
									<SimpleTag text={label} color={memoizeGetAutoGeneratedColorForLabelsTag(label)} />
								</Box>
							);
						}

						const { backgroundColor: borderColor } = getColorMetadata(
							memoizeGetAutoGeneratedColorForLabels(label),
						);
						return (
							<span
								css={tagBorderStyles}
								// eslint-disable-next-line jira/react/no-style-attribute, @typescript-eslint/consistent-type-assertions
								style={{ '--tag-border-color': borderColor } as React.CSSProperties}
								key={label}
							>
								<SimpleTag text={label} />
							</span>
						);
					}

					return <SimpleTag key={label} text={label} />;
				}),
			[labels],
		);

		const defaultOptions = useMemo(
			() =>
				defaultSuggestions
					.filter((suggestion) => labels.indexOf(suggestion.label) === -1)
					.map((suggestion) => ({
						label: suggestion.label,
						value: suggestion.label,
					})),
			[defaultSuggestions, labels],
		);

		const styles = useMemo(
			() => ({
				option: (base?: Record<string, string>) => ({
					...base,
					textOverflow: 'ellipsis',
					overflow: 'hidden',
				}),
			}),
			[],
		);

		const labelsValueStyles: StylesConfig<SelectOption, true> = useMemo(
			() => ({
				multiValue: (base, { data }) => {
					const { backgroundColor: borderColor } = getColorMetadata(
						memoizeGetAutoGeneratedColorForLabels(data.label),
					);
					return {
						...base,
						border: `1px solid ${borderColor}`,
						'& > div > div > div': {
							paddingLeft: 0,
							paddingRight: 0,
						},
						...(!isVisualRefreshEnabled()
							? {
									borderRadius: token('border.radius.100'),
									backgroundColor: 'transparent',
									'& > div': {
										paddingLeft: token('space.050'),
										paddingRight: 0,
										paddingTop: 0,
										paddingBottom: 0,
									},
								}
							: {}),
					};
				},
				multiValueLabel: (base) => ({
					...base,
					'& > div > div': {
						borderColor: 'transparent',
					},
				}),
			}),
			[],
		);

		const labelsRemoveIconStyles: StylesConfig<SelectOption, true> = useMemo(
			() => ({
				multiValueRemove: (base) => ({
					...base,
					'& > div > span > svg': {
						transform: 'scale(1.5)',
						fill: 'none',
					},
					'& > div > span > svg > g > path': {
						d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
						transform: 'translate(5px, 6px)',
						stroke: token('color.text'),
						strokeWidth: '1.5',
					},
				}),
			}),
			[],
		);

		const selectStyles = useMemo(() => {
			if (
				expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)
			) {
				if (!isVisualRefreshEnabled()) {
					return { ...styles, ...labelsValueStyles, ...labelsRemoveIconStyles };
				}
				return { ...styles, ...labelsValueStyles };
			}
			if (isVisualRefreshEnabled()) {
				return styles;
			}
			return {};
		}, [labelsRemoveIconStyles, labelsValueStyles, styles]);

		const formatCreateLabel = useCallback(
			(inputValue_: string): string => `${inputValue_} (${formatMessage(messages.createNewItem)})`,
			[formatMessage],
		);

		const noOptionsMessage = useCallback(
			(args: { inputValue: string }) =>
				labels.find((label) => label === args.inputValue)
					? formatMessage(messages.duplicate)
					: formatMessage(messages.noResults),
			[formatMessage, labels],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div {...triggerProps}>
					<TagListWrapper
						isEditable={isEditable}
						data-testid="business-list.ui.list-view.labels-cell.tag-list-wrapper"
					>
						<Tooltip content={labels.join(', ')} position="top">
							{staticContent}
						</Tooltip>
					</TagListWrapper>
				</div>
			),
			[labels, isEditable, staticContent],
		);

		const popupContent = useCallback(
			() => (
				<CellContentWrapper preferredWidth={width - 1}>
					<VisuallyHidden>
						<Label htmlFor="business-list.ui.list-view.labels-cell.popup-content-lazy-creatable-select">
							{formatMessage(messages.labelMultipleOptionsLabel)}
						</Label>
					</VisuallyHidden>
					<LazyCreatableSelect
						isMulti
						isClearable
						autoFocus
						menuIsOpen={menuIsOpen}
						placeholder={formatMessage(messages.placeholder)}
						value={labels.map((label) => ({ value: label, label }))}
						onChange={handleLabelsChange}
						loadOptions={getSuggestions}
						defaultOptions={defaultOptions}
						onCreateOption={handleCreateOption}
						formatCreateLabel={formatCreateLabel}
						{...(expVal(
							'adding-color-to-labels-field-in-jira-experiment',
							'isColoredLabelsEnabled',
							false,
						)
							? { isColored: true }
							: {})}
						noOptionsMessage={noOptionsMessage}
						closeMenuOnSelect={false}
						onInputRef={handleInputRef}
						inputId="business-list.ui.list-view.labels-cell.popup-content-lazy-creatable-select"
						label={formatMessage(messages.selectBoxLabel)}
						tabSelectsValue
						inputValue={inputValue}
						onInputChange={onInputChange}
						styles={selectStyles}
						components={{
							...(expVal(
								'adding-color-to-labels-field-in-jira-experiment',
								'isColoredLabelsEnabled',
								false,
							) && {
								MultiValueContainer: (props) => (
									<components.MultiValueContainer
										{...props}
										xcss={cx(
											multiValueStyles.root,
											!isVisualRefreshEnabled() && multiValueStyles.isVisualRefreshDisabled,
										)}
									/>
								),
								MultiValueLabel: (props) => (
									<components.MultiValueLabel
										{...props}
										xcss={!isVisualRefreshEnabled() && multiValueLabelStyles.root}
									/>
								),
								MultiValueRemove: (props) => (
									<components.MultiValueRemove
										{...props}
										xcss={!isVisualRefreshEnabled() && multiValueRemoveStyles.root}
									/>
								),
							}),
						}}
					/>
				</CellContentWrapper>
			),
			[
				width,
				formatMessage,
				menuIsOpen,
				labels,
				handleLabelsChange,
				getSuggestions,
				defaultOptions,
				handleCreateOption,
				formatCreateLabel,
				noOptionsMessage,
				handleInputRef,
				inputValue,
				onInputChange,
				selectStyles,
			],
		);

		const popupComponent = useMemo(
			() =>
				getPopupComponentWithHeight({
					numOptions: Math.min(8, defaultSuggestions.length),
					hasFooter: true,
				}),
			[defaultSuggestions],
		);

		const cellContent = (
			<>
				{isHovering || editMode || isSelected ? (
					<Popup
						isOpen={editMode}
						placement="bottom-start"
						autoFocus={false}
						offset={POPUP_OFFSET_NEW}
						popupComponent={popupComponent}
						content={popupContent}
						trigger={popupTrigger}
						testId="business-list.ui.list-view.labels-cell.labels-menu"
					/>
				) : (
					<TagListWrapper>{staticContent}</TagListWrapper>
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<CellWrapper
				isEditable={isEditable}
				testId="business-list.ui.list-view.labels-cell.cell-container"
				hasPadding={false}
			>
				<ContentWrapper>{cellContent}</ContentWrapper>
			</CellWrapper>
		);
	},
);

export default LabelsCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Flex xcss={[wrapperStyles]}>{children}</Flex>
);

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});

const tagContainerStyles = xcss({
	display: 'inline-flex',
	minWidth: '45px',
});

const tagBorderStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		backgroundColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: '1px',
		borderColor: 'var(--tag-border-color)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span > a': {
		color: token('color.text'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span > a:hover': {
		color: token('color.text'),
	},
});
